import React, {JSX, useEffect, useMemo, useRef, useState, useId} from 'react';
import {t} from 'i18next';
import {Box, Button, Typography} from '@mui/material';
import ArrAccordion from '../../common/arr-accordion/ArrAccordion';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import ArrTreeView from '../../common/arr-tree-view/ArrTreeView';
import {ISelectItem} from '../../../shared/types';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import './ExplorerArrSourceSelectedContainer.scss';
import {transition_time_default} from '../../../shared/constants';
import ArrSelectionChip from '../../common/arr-selection-chip/ArrSelectionChip';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {conceptExpUrl} from '../communication-explorer/utils';

export type SelectedSource = {
  id: number;
  sectionId: string;
  sectionLabel: string;
  label: string;
  nodeId?: string;
};

export type SourceItem = {
  id: string;
  label: string;
  data?: any[];
  [key: string]: any;
};

export interface ArrSourceSelectedContainerProps {
  sources: SourceItem[];
  onSelectionsUpdate?: (map: Record<string, any[] | null>) => void;
  sourceID: string[];
  sourceType: string;
}

const ExplorerArrSourceSelectedContainer: React.FC<
  ArrSourceSelectedContainerProps
> = ({sources, onSelectionsUpdate, sourceID, sourceType}) => {
  const [selectedSources, setSelectedSources] = useState<SelectedSource[]>([]);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [_panelOpen, setPanelOpen] = useState<boolean>(true);
  const [selectSourceKey, setSelectSourceKey] = useState<number>(0);
  const [selectedSourceType, setSelectedSourceType] =
    useState<ISelectItem | null>(null);
  const [selectedIdsBySection, setSelectedIdsBySection] = useState<
    Record<string, string[] | null>
  >({});
  const [, setFullSelectionsBySection] = useState<Record<string, any[] | null>>(
    {}
  );
  const idRef = useRef<number>(1);
  const [allowHeavyCompute, setAllowHeavyCompute] = useState(false);
  const treeCacheRef = useRef<Record<string, any[]>>({});
  const lookupCacheRef = useRef<Record<string, Record<string, any>>>({});
  const [preparedExpandedIds, setPreparedExpandedIds] = useState<
    string[] | null
  >(null);
  const [expandReady, setExpandReady] = useState(false);
  const expandQueueRef = useRef<any[] | null>(null);
  const expandResultRef = useRef<string[]>([]);
  const ricIdRef = useRef<number | null>(null);
  const uId = useId();

  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const unmountTimerRef = useRef<number | null>(null);
  const openSyncIdRef = useRef<number | null>(null);
  const computeDelayRafRef = useRef<number | null>(null);
  const {sourceId, backSourcePath, sourceSelectionText} = useSelector(
    (state: RootState) => state.comExplorer
  );

  const openDrawer = () => {
    if (unmountTimerRef.current) {
      cancelAnimationFrame(unmountTimerRef.current);
      unmountTimerRef.current = null;
    }
    if (!drawerOpen) setDrawerOpen(true);
    if (openSyncIdRef.current) cancelAnimationFrame(openSyncIdRef.current);
    openSyncIdRef.current = requestAnimationFrame(() => {
      openSyncIdRef.current = requestAnimationFrame(() => {
        setDrawerVisible(true);
      });
    });
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setAllowHeavyCompute(false);
    if (computeDelayRafRef.current) {
      cancelAnimationFrame(computeDelayRafRef.current);
      computeDelayRafRef.current = null;
    }
    const start = performance.now();
    const duration = transition_time_default + 80;
    const step = () => {
      const elapsed = performance.now() - start;
      if (elapsed >= duration) {
        setDrawerOpen(false);
        unmountTimerRef.current = null;
      } else {
        unmountTimerRef.current = requestAnimationFrame(step);
      }
    };
    unmountTimerRef.current = requestAnimationFrame(step);
  };

  useEffect(() => {
    setPanelOpen(!drawerVisible);
  }, [drawerVisible]);

  useEffect(() => {
    treeCacheRef.current = {};
    lookupCacheRef.current = {};
    if (!sourceType) return;
    const currentSource =
      sources
        .filter((s) => s?.label?.toLowerCase() === sourceType.toLowerCase())
        .map((s) => ({id: s.id, name: s.label}))[0] || null;
    setSelectedSourceType(currentSource);

    if (!sourceID || !currentSource) return;
    setSelectedIdsBySection({[sourceType.toLowerCase()]: sourceID});

    const currentId = currentSource.id?.toString?.() ?? '';
    const currentSelection = sources.find((s) => s.id === currentId) || null;

    if (!currentSelection) return;

    const lookUp = buildLookupMap(currentSelection?.data ?? []);
    const entries = sourceID
      .map((id) => {
        const obj = lookUp[id];
        const label = pickNodeLabel(obj);
        return label ? {label, nodeId: id} : null;
      })
      .filter(Boolean) as {label: string; nodeId: string}[];
    const newSelection = entries.map((e) => ({
      id: idRef.current++,
      sectionId: currentId,
      sectionLabel: currentSelection.label,
      label: e.label,
      nodeId: e.nodeId,
    }));
    setSelectedSources(newSelection);
    const newIds = newSelection.map((s) => String(s.nodeId));
    setSelectedIdsBySection((prev) => {
      return {
        ...prev,
        [currentId]: newIds,
      };
    });
    onSelectionsUpdate?.({
      [currentId]: [
        {
          sourceName: getSectionMeta(currentId).sourceName,
          source: newIds,
          navigationSourceType: getSectionMeta(currentId).navigationSourceType,
        },
      ],
    });
  }, [sources, sourceType, sourceID]);

  useEffect(() => {
    if (drawerVisible) {
      setAllowHeavyCompute(false);
      const minDelay = Math.max(
        180,
        Math.floor(transition_time_default * 0.65)
      );
      const start = performance.now();
      const tick = () => {
        if (!drawerVisible) return;
        const elapsed = performance.now() - start;
        if (elapsed >= minDelay) {
          setAllowHeavyCompute(true);
          computeDelayRafRef.current = null;
        } else {
          computeDelayRafRef.current = requestAnimationFrame(tick);
        }
      };
      computeDelayRafRef.current = requestAnimationFrame(tick);
      return () => {
        if (computeDelayRafRef.current) {
          cancelAnimationFrame(computeDelayRafRef.current);
          computeDelayRafRef.current = null;
        }
      };
    } else {
      setAllowHeavyCompute(false);
      setPreparedExpandedIds(null);
      setExpandReady(false);
      expandQueueRef.current = null;
      expandResultRef.current = [];
      if (ricIdRef.current != null) {
        const w: any = window as any;
        if (w.cancelIdleCallback) w.cancelIdleCallback(ricIdRef.current);
        else cancelAnimationFrame(ricIdRef.current);
        ricIdRef.current = null;
      }
    }
  }, [drawerVisible, transition_time_default]);

  const sourceOptions: ISelectItem[] = sources
    .filter((s) => !(Array.isArray(s.data) && s.data.length === 0))
    .map((s) => ({id: s.id, name: s.label}));

  const pickNodeLabel = (node: any) =>
    node?.text ??
    node?.securityGroupLabel ??
    node?.projectLabel ??
    node?.label ??
    node?.name ??
    '';

  const getNormalizedId = (n: any): string =>
    String(
      n?.id ??
        n?.value ??
        n?.securityGroupId ??
        n?.projectId ??
        n?.tagClassificationId ??
        ''
    );

  const buildLookupMap = (flatList: any[] = []): Record<string, any> => {
    const map: Record<string, any> = {};
    (flatList ?? []).forEach((n) => {
      const id = getNormalizedId(n);
      if (id) map[id] = n;
    });
    return map;
  };

  const buildNestedTree = (flatList: any[] = []): any[] => {
    const nodeMap: Record<string, any> = {};
    const roots: any[] = [];
    (flatList ?? []).forEach((node) => {
      const id = getNormalizedId(node);
      const parentId = node?.parent != null ? String(node.parent) : '';
      nodeMap[id] = {...node, id, parentId, children: []};
    });
    Object.values(nodeMap).forEach((node: any) => {
      if (node.parentId && nodeMap[node.parentId]) {
        nodeMap[node.parentId].children.push(node);
      } else {
        roots.push(node);
      }
    });
    for (let root of roots) {
      if (root.isGroup && root.children.length === 0) {
        root.disabled = true;
      }
    }
    return roots;
  };

  const mapToTreeItems = (nodes: any[] = []): any[] =>
    (nodes ?? []).map((n: any) => {
      const id = getNormalizedId(n);
      const label = pickNodeLabel(n);
      const children = Array.isArray(n?.children)
        ? mapToTreeItems(n.children)
        : undefined;
      return {
        id,
        label,
        fileType:
          children && children.length
            ? `${t('reports.tally.sourceContainer.folder')}`
            : `${t('reports.tally.sourceContainer.search')}`,
        disabled: !!n?.disabled,
        count: n?.count,
        children,
      };
    });

  const getSectionMeta = (sectionId: string) => {
    switch (sectionId) {
      case 'securityGroups':
        return {
          sourceName: 'SECURITY GROUPS',
          navigationSourceType: 'SECGROUP',
        };
      case 'searches':
        return {sourceName: 'SEARCHES', navigationSourceType: 'SEARCH'};
      case 'projects':
        return {sourceName: 'PROJECTS', navigationSourceType: 'PROJECT'};
      case 'folders':
        return {sourceName: 'FOLDERS', navigationSourceType: 'FOLDER'};
      case 'assignments':
        return {sourceName: 'ASSIGNMENTS', navigationSourceType: 'ASSIGNMENT'};
      default:
        return {
          sourceName: sectionId?.toUpperCase?.() ?? '',
          navigationSourceType: sectionId?.toUpperCase?.() ?? '',
        };
    }
  };

  const handleIdsChangeForSection = (
    sectionId: string,
    ids: string[] | null,
    lookup: Record<string, any>
  ) => {
    setSelectedIdsBySection((prev) => {
      const nextIdsMap = {...prev, [sectionId]: ids};
      const nextFull = ids ? ids.map((id) => lookup[id]).filter(Boolean) : null;
      setFullSelectionsBySection((prevFull) => ({
        ...prevFull,
        [sectionId]: nextFull,
      }));
      return nextIdsMap;
    });
  };

  const handleSaveSelection = (
    sectionId: string,
    sectionLabel: string,
    ids: string[] | null,
    lookup: Record<string, any>
  ) => {
    if (!ids || !ids.length) {
      closeDrawer();
      return;
    }
    const entries = ids
      .map((id) => {
        const obj = lookup[id];
        const label = pickNodeLabel(obj);
        return label ? {label, nodeId: id} : null;
      })
      .filter(Boolean) as {label: string; nodeId: string}[];
    if (!entries.length) {
      closeDrawer();
      return;
    }
    setSelectedSources(() => {
      const next = entries.map((e) => ({
        id: idRef.current++,
        sectionId,
        sectionLabel,
        label: e.label,
        nodeId: e.nodeId,
      }));
      return next;
    });
    setSelectedIdsBySection(() => ({[sectionId]: ids}));
    setFullSelectionsBySection(() => ({
      [sectionId]: ids.map((id) => lookup[id]).filter(Boolean),
    }));
    const cleanedIds = (ids ?? []).map(String).filter((v) => /^-?\d+$/.test(v));
    onSelectionsUpdate?.({
      [sectionId]: [
        {
          sourceName: getSectionMeta(sectionId).sourceName,
          source: cleanedIds,
          navigationSourceType: getSectionMeta(sectionId).navigationSourceType,
        },
      ],
    });
    closeDrawer();
  };

  const handleRemoveChip = (source: SelectedSource) => {
    setSelectedSources((prev) => {
      const next = prev.filter((i) => i.id !== source.id);
      if (next.length === 0) onSelectionsUpdate?.({});
      return next;
    });
    const current = selectedIdsBySection[source.sectionId] ?? [];
    const nextIds = Array.isArray(current)
      ? current.filter((id) => String(id) !== String(source.nodeId))
      : null;
    const section = sources.find((s) => s.id === source.sectionId);
    const lookup = buildLookupMap(section?.data ?? []);
    handleIdsChangeForSection(
      source.sectionId,
      nextIds && nextIds.length ? nextIds : null,
      lookup
    );
    setSelectSourceKey((k) => k + 1);
  };

  const currentSectionId = selectedSourceType?.id?.toString?.() ?? '';
  const currentSection = useMemo<SourceItem | null>(
    () => sources.find((s) => s.id === currentSectionId) ?? null,
    [sources, currentSectionId]
  );

  const currentLookup = useMemo(() => {
    const sid = currentSection?.id ?? '';
    if (!sid) return {};
    if (!allowHeavyCompute) return lookupCacheRef.current[sid] ?? {};
    if (!lookupCacheRef.current[sid])
      lookupCacheRef.current[sid] = buildLookupMap(currentSection?.data ?? []);
    return lookupCacheRef.current[sid];
  }, [currentSection, allowHeavyCompute]);

  const treeItems = useMemo(() => {
    const sid = currentSection?.id ?? '';
    if (!sid) return [];
    if (!allowHeavyCompute) return treeCacheRef.current[sid] ?? [];
    if (!treeCacheRef.current[sid]) {
      const rawData = currentSection?.data ?? [];
      const nestedData = buildNestedTree(rawData);
      treeCacheRef.current[sid] = mapToTreeItems(nestedData);
    }
    return treeCacheRef.current[sid];
  }, [currentSection, allowHeavyCompute]);

  const rootFolderIds = useMemo(() => {
    if (!allowHeavyCompute) return [];
    return (treeItems ?? [])
      .filter((n: any) => n?.children && n.children.length)
      .map((n: any) => n.id);
  }, [allowHeavyCompute, treeItems]);

  useEffect(() => {
    if (!drawerVisible || !allowHeavyCompute) return;
    setPreparedExpandedIds(null);
    setExpandReady(false);
    expandResultRef.current = [];
    expandQueueRef.current = [...treeItems];
    const run = (deadline: {timeRemaining: () => number}) => {
      if (!expandQueueRef.current) return;
      let processed = 0;
      while (
        expandQueueRef.current.length &&
        (deadline.timeRemaining() > 1 || processed < 120)
      ) {
        const node = expandQueueRef.current.shift();
        if (node && node.children && node.children.length) {
          expandResultRef.current.push(node.id);
          for (let i = 0; i < node.children.length; i++)
            expandQueueRef.current.push(node.children[i]);
        }
        processed++;
        if (processed >= 4000) break;
      }
      if (expandQueueRef.current.length) {
        const w: any = window as any;
        if (w.requestIdleCallback) {
          ricIdRef.current = w.requestIdleCallback(run);
        } else {
          ricIdRef.current = requestAnimationFrame(() =>
            run({timeRemaining: () => 0})
          );
        }
      } else {
        setPreparedExpandedIds(expandResultRef.current.slice());
        setExpandReady(true);
        setSelectSourceKey((k) => k + 1);
        expandQueueRef.current = null;
        expandResultRef.current = [];
        ricIdRef.current = null;
      }
    };
    const w: any = window as any;
    if (w.requestIdleCallback) {
      ricIdRef.current = w.requestIdleCallback(run);
    } else {
      ricIdRef.current = requestAnimationFrame(() =>
        run({timeRemaining: () => 0})
      );
    }
    return () => {
      if (ricIdRef.current != null) {
        if (w.cancelIdleCallback) w.cancelIdleCallback(ricIdRef.current);
        else cancelAnimationFrame(ricIdRef.current);
        ricIdRef.current = null;
      }
    };
  }, [drawerVisible, allowHeavyCompute, treeItems]);

  const defaultExpandedIds = useMemo(() => {
    return undefined as unknown as string[] | undefined;
  }, [allowHeavyCompute, expandReady, preparedExpandedIds, rootFolderIds]);
  const isSelectSourceDisabled = useMemo(() => {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (sourceId && backSourcePath !== currentPath) {
      return true;
    }
    if (sourceType === 'DOCLIST') return true;
    return false;
  }, [backSourcePath, sourceType]);

  const getLabel = () => {
    if (sourceSelectionText) {
      return sourceSelectionText;
    }
    if (sourceType === 'DOCLIST') {
      const currentPath =
        `${window.location.pathname}${window.location.search}`.toLowerCase();
      if (currentPath.includes(conceptExpUrl.toLowerCase()))
        return t('reports.conceptExplorer.fromConceptExplorer');
      else {
        return t('reports.communicationExplorer.fromCommunicationExplorer');
      }
    }
    return '';
  };

  const isNotSameUrl = useMemo(() => {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (backSourcePath && backSourcePath === currentPath) {
      return false;
    }
    return true;
  }, [backSourcePath]);

  const content: JSX.Element = useMemo(
    () => (
      <Box className="arr-chips-container" data-testid="arr-chips-container">
        {selectedSources.length > 0 || isSelectSourceDisabled ? (
          <>
            {/* Header */}
            <Typography
              variant="subtitle2"
              className="chip-count-header"
              data-testid="chip-count-header"
              sx={{mb: 1}} // optional spacing only above chips
            >
              {t('common.selected')} (
              {isSelectSourceDisabled ? '1' : selectedSources.length})
            </Typography>

            {/* Scrollable Chips */}
            <Box className="boxContainer">
              {isSelectSourceDisabled ? (
                <ArrSelectionChip
                  key="DOCLIST"
                  filterData={{
                    id: 'DOCLIST',
                    label: getLabel(),
                    isSelected: false,
                  }}
                  onClick={() => {}}
                  data-testid={`arr-chip-DOCLIST`}
                  variant="v2"
                  isFilterInactive={true}
                  noIcon={true}
                />
              ) : (
                selectedSources.map((source, index) => (
                  <ArrSelectionChip
                    key={`${source.sectionId}-${source.id}`}
                    filterData={{
                      id: `${source.sectionId}:${source.id}`,
                      label: `${source.label}`,
                      isSelected: false,
                    }}
                    onClick={() => handleRemoveChip(source)}
                    data-testid={`arr-chip-${source.sectionId}-${source.id}`}
                    variant="v2"
                    showCloseIcon={true}
                    labelTextTestId={`${source.sectionId}-${index}`}
                  />
                ))
              )}
            </Box>
          </>
        ) : (
          <div
            className="no-source-container"
            data-testid={`explorer-arr-source-selected-container-div`}
          >
            <Typography
              variant="body2"
              data-testid="arr-no-selection-message"
              className="unavailable-text"
            >
              {''}
            </Typography>
          </div>
        )}
      </Box>
    ),
    [selectedSources, isSelectSourceDisabled]
  );

  const accordionSections = [
    {
      id: 'sourceContainer',
      title: (
        <Box
          className="arr-accordion-title"
          data-testid={`arr-accordion-title-${uId}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
          }}
        >
          {/* Header */}
          <Box className="source-header-row">
            <Typography
              variant="subtitle2"
              tabIndex={0}
              className="arr-dropdown-header"
              data-testid={`arr-dropdown-header-${uId}`}
            >
              {t('reports.concepts.selectSources')}
            </Typography>
            <ArrTooltipWrapper
              data-testid={`ceh-tooltip-wrapper-${uId}`}
              title={t('reports.concepts.tooltip.title')}
              trigger={
                <div
                  tabIndex={0}
                  data-testid={`explorer-arr-source-selected-container-div-btn-${uId}`}
                >
                  <InfoIcon
                    className="view-user-info-icon"
                    data-testid={`ceh-info-icon-${uId}`}
                  />
                </div>
              }
            >
              <div data-testid={`ceh-tooltip-content-${uId}`}>
                <p
                  data-testid={`explorer-arr-source-selected-container-content-${uId}`}
                >
                  {t('reports.concepts.tooltip.content')}
                </p>
              </div>
            </ArrTooltipWrapper>
          </Box>
          <ArrSingleSelectDropdown
            dropdownLabel={`${t('reports.concepts.selectSources')} *`}
            dropdownItems={sourceOptions}
            selectedValues={selectedSourceType}
            onSelectionChange={(item: ISelectItem | null) => {
              const selected = sources.find((s) => s.id === item?.id);
              if (selected) {
                setSelectedSourceType(item);
                openDrawer();
                setSelectSourceKey((k) => k + 1);
              }
            }}
            sizeVariant="medium"
            dataTestId={`asd-explore-arr-source-type-dropdown-${uId}`}
            disabled={isSelectSourceDisabled}
          />
        </Box>
      ),
      content: content,
    },
  ];

  const headerTitle = (
    <Box className="flexCenter">
      <Box className="flexBoxWhite">
        <Typography
          variant="caption"
          className="captionBlock"
          data-testid={`explorer-arr-source-selected-container-source`}
        >
          {t('ingestions.mapped.tileMenu.source')}:
        </Typography>
        <Typography
          variant="h6"
          className="typographyEllipsis"
          data-testid={`explorer-arr-source-selected-container-section-label-${currentSectionId}`}
        >
          {currentSection?.label}
        </Typography>
      </Box>
      <Box className="button-container">
        <Button
          variant="contained"
          className="cancel-btn"
          onClick={() => closeDrawer()}
          data-testid={`arr-cancel-button-${currentSectionId}`}
        >
          <Typography
            className="cancel-button-text"
            data-testid={`explorer-arr-source-selected-container-cancel`}
          >
            {t('common.cancel')}
          </Typography>
        </Button>
        <Button
          variant="contained"
          className={`select-btn ${
            !Array.isArray(selectedIdsBySection[currentSectionId]) ||
            selectedIdsBySection[currentSectionId]?.length === 0
              ? 'inactive'
              : ''
          }`}
          onClick={() =>
            handleSaveSelection(
              currentSectionId,
              currentSection?.label ?? t('ingestions.mapped.tileMenu.source'),
              Array.isArray(selectedIdsBySection[currentSectionId])
                ? selectedIdsBySection[currentSectionId]
                : null,
              currentLookup
            )
          }
          disabled={
            !Array.isArray(selectedIdsBySection[currentSectionId]) ||
            selectedIdsBySection[currentSectionId]?.length === 0
          }
          data-testid={`arr-save-button-${currentSectionId}`}
        >
          <Typography
            className={`select-button-text ${
              !Array.isArray(selectedIdsBySection[currentSectionId]) ||
              selectedIdsBySection[currentSectionId]?.length === 0
                ? 'inactive'
                : ''
            }`}
            data-testid={`explorer-arr-source-selected-container-select-${currentSectionId}`}
          >
            {t('common.select')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderDrawer = (isOpen: boolean): JSX.Element => (
    <ArrDrawer
      transition={transition_time_default}
      isOpen={drawerVisible}
      onClose={() => {}}
      title={headerTitle as unknown as string}
      showButton={false}
      size="small"
      side="right"
      trapFocus={true}
      data-testid="arr-source-drawer"
    >
      <Box
        className="drawerContent"
        data-testid="arr-drawer-content"
        sx={{height: '100%'}}
      >
        <Box
          className="arr-tree-container"
          data-testid={`arr-tree-container-${currentSectionId}`}
          sx={{height: '100%', overflowY: 'auto'}}
        >
          <ArrTreeView
            key={selectSourceKey + (expandReady ? 1 : 0)}
            itemsList={treeItems}
            isShowCheckbox={true}
            multipleSelection={true}
            treeSelectedItems={
              Array.isArray(selectedIdsBySection[currentSectionId])
                ? selectedIdsBySection[currentSectionId]
                : null
            }
            setSelectedItem={(val: any) => {
              const ids = Array.isArray(val)
                ? val.map(String)
                : val
                  ? [String(val)]
                  : [];
              handleIdsChangeForSection(
                currentSectionId,
                ids.length ? ids : null,
                currentLookup
              );
            }}
            defaultExpandedIds={defaultExpandedIds as unknown as string[]}
            data-testid={`arr-treeview-${currentSectionId}`}
            isShowFileType={false}
          />
        </Box>
      </Box>
    </ArrDrawer>
  );

  return (
    <>
      <ArrAccordion
        sections={accordionSections}
        expandedSection="sourceContainer"
        onToggle={(id: string) => setPanelOpen(id === 'sourceContainer')}
        dataTestId="explorer-selected-source"
      />
      {drawerOpen && renderDrawer(drawerOpen)}
    </>
  );
};

export default ExplorerArrSourceSelectedContainer;
